import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessagesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUserType, setFilterUserType] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [replies, setReplies] = useState({});
  const [byUserType, setByUserType] = useState({});
  const [byCategory, setByCategory] = useState({});

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/admin/messages${filterStatus ? `?status=${filterStatus}` : ""}`;
      const r = await fetch(url, { headers: auth() });
      if (r.ok) {
        const d = await r.json();
        setRows(d.messages || []);
        setByUserType(d.by_user_type || {});
        setByCategory(d.by_category || {});
      }
    } finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return rows.filter(m => {
      if (filterUserType && (m.user_type_context || "me") !== filterUserType) return false;
      if (filterCategory && (m.category || "general") !== filterCategory) return false;
      return true;
    });
  }, [rows, filterUserType, filterCategory]);

  const reply = async (mid) => {
    const text = (replies[mid] || "").trim();
    if (!text) { toast.error("Type a reply first"); return; }
    const r = await fetch(`${API}/admin/messages/${mid}/reply`, {
      method: "POST",
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify({ text, status: "answered" }),
    });
    if (r.ok) {
      toast.success("Reply sent");
      setReplies(p => ({ ...p, [mid]: "" }));
      load();
    } else toast.error("Send failed");
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="admin-messages-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold flex items-center gap-2"><MessageSquare className="w-5 h-5" /> Messages</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <FilterButton label="All user types" values={Object.keys(byUserType)} selected={filterUserType} onChange={setFilterUserType} counts={byUserType} testid="msg-filter-ut" />
        <FilterButton label="All categories" values={Object.keys(byCategory)} selected={filterCategory} onChange={setFilterCategory} counts={byCategory} testid="msg-filter-cat" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="msg-filter-status">
          <option value="">All status</option>
          <option value="open">Open</option>
          <option value="answered">Answered</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-10 text-gray-400" /> : filtered.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-10">No messages match.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map(m => (
            <Card key={m.message_id} data-testid={`msg-row-${m.message_id}`}>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Badge className="bg-indigo-50 text-indigo-700 border-0">{m.user_type_context || "me"}</Badge>
                  <Badge className="bg-pink-50 text-pink-700 border-0">{m.category || "general"}</Badge>
                  <span>{m.from_name || m.from_email || m.recipient_user_id}</span>
                  <span className="text-gray-400">· {(m.created_at || "").slice(0, 10)}</span>
                  {m.status && <Badge className="bg-gray-100 text-gray-600 border-0">{m.status}</Badge>}
                </div>
                <p className="text-sm whitespace-pre-wrap">{m.body || m.text}</p>
                {(m.replies || []).map(r => (
                  <div key={r.reply_id} className="ml-4 pl-3 border-l-2 border-indigo-200 text-xs">
                    <p className="text-gray-500">↪ Admin reply · {(r.created_at || "").slice(0, 10)}</p>
                    <p className="text-sm whitespace-pre-wrap">{r.text}</p>
                  </div>
                ))}
                <div className="flex gap-2 pt-1">
                  <Textarea
                    rows={2}
                    placeholder="Reply..."
                    value={replies[m.message_id] || ""}
                    onChange={e => setReplies(p => ({ ...p, [m.message_id]: e.target.value }))}
                    data-testid={`msg-reply-input-${m.message_id}`}
                  />
                  <Button onClick={() => reply(m.message_id)} data-testid={`msg-reply-send-${m.message_id}`}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Send
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterButton({ label, values, selected, onChange, counts, testid }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <button
        onClick={() => onChange("")}
        className={`text-xs px-2.5 py-1 rounded-full border ${!selected ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-200"}`}
        data-testid={`${testid}-all`}
      >{label}</button>
      {values.map(v => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`text-xs px-2.5 py-1 rounded-full border ${selected === v ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-200"}`}
          data-testid={`${testid}-${v}`}
        >{v} <span className="opacity-60">({counts[v]})</span></button>
      ))}
    </div>
  );
}
